// Áreas de atuação de uma turma dentro da instituição (ver coluna `area` em
// `atividades`, backend/atividades.js). Lista única usada tanto no formulário
// de turma (Turmas.js) quanto nos filtros da Grade por Turma (GradeTurmas.js)
// — evita duas listas divergentes.
//
// Os LABELS abaixo são só o valor padrão/fallback — o nome de exibição de
// cada área é editável pelo master na tela "Áreas" (AreasConfig.js) e fica
// salvo em backend/areas-config.js (tabela areas_config). `carregarLabelsAreas()`
// busca a configuração atual e sobrescreve os labels destes MESMOS objetos
// (mutação in-place, nunca reatribuição — todo mundo que importou AREAS/
// AREA_LABEL segura essa referência) assim que o app carrega (ver
// AuthContext.js); os VALUES (`educacional`, `esportivo` etc.) nunca mudam,
// só o texto mostrado.
// Paleta padrão de área do sistema inteiro — as 5 cores foram escolhidas
// deliberadamente afastadas entre si no círculo de matizes (azul/verde/roxo/
// laranja/rosa) pra nunca serem confundidas num badge ou numa bolinha de
// calendário. Antes tecnológico usava ciano (perto demais do azul de
// educacional) e capelania usava âmbar (perto demais do laranja/vermelho) —
// trocados por laranja e rosa. Qualquer tela que precise da cor de uma área
// deve ler daqui (`.ativo`); não duplique esse mapeamento — as duas exceções
// conhecidas hoje (frontend/src/Components/AreaFrequencyChart.jsx e
// backend/relatorio-agenda-anual-pdf.js) têm motivo próprio documentado nelas
// e precisam ser atualizadas à mão se essa paleta mudar de novo.
import { API_URL } from '../api/config';
export const AREAS = [
  {
    value: 'educacional',
    label: 'Educação por Princípios',
    ativo: 'bg-blue-600 text-white',
    inativo: 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600',
  },
  {
    value: 'esportivo',
    label: 'Esporte',
    ativo: 'bg-green-600 text-white',
    inativo: 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600',
  },
  {
    value: 'cultural',
    label: 'Arte e Cultura',
    ativo: 'bg-purple-600 text-white',
    inativo: 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600',
  },
  {
    value: 'tecnologico',
    label: 'Educação Profissional',
    ativo: 'bg-orange-600 text-white',
    inativo: 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600',
  },
  {
    value: 'capelania',
    label: 'Capelania',
    ativo: 'bg-pink-600 text-white',
    inativo: 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600',
  },
];

export const AREA_LABEL = Object.fromEntries(AREAS.map((a) => [a.value, a.label]));

// Aplica um mapa { area: label } vindo do backend em AREAS/AREA_LABEL, por
// mutação — usada tanto na carga inicial (ver AuthContext.js) quanto logo
// após o master salvar mudanças na tela "Áreas", pra refletir na hora sem
// precisar de F5.
export function aplicarLabelsAreas(labels) {
  if (!labels || typeof labels !== 'object') return;
  for (const area of AREAS) {
    if (labels[area.value]) area.label = labels[area.value];
  }
  Object.assign(AREA_LABEL, labels);
}

// Busca a configuração atual no backend e aplica. Silenciosa em erro (fica
// com os labels padrão/anteriores) — nomenclatura de área não deve travar o
// carregamento do app.
export async function carregarLabelsAreas(token) {
  try {
    const res = await fetch(`${API_URL}/api/areas`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const body = await res.json();
    const labels = body?.data ?? body;
    aplicarLabelsAreas(labels);
  } catch (err) {
    console.error('Erro ao carregar nomes de áreas:', err);
  }
}
